import type { StudyTaskTargetEngagementSelectDto } from '@@types/api.types';
import type { MeiaTaskEngagementEntryFormValues } from '@schemas/meia.schema';
import { generateMeiaLocalId } from './meia-measures.util';

type DiffEngagementEntriesParams = {
  baselineById: Map<string, MeiaTaskEngagementEntryFormValues>;
  entries: MeiaTaskEngagementEntryFormValues[];
};

type EngagementEntriesDiff = {
  /** New rows without a server id. */
  creates: MeiaTaskEngagementEntryFormValues[];
  /** Rows whose technology changed: the engagement is recreated under the new technology. */
  moves: MeiaTaskEngagementEntryFormValues[];
  /** Rows with a server id whose editable fields changed. */
  updates: MeiaTaskEngagementEntryFormValues[];
};

export const buildEmptyEngagementEntry = (params?: {
  taskTechnologyId?: string;
}): MeiaTaskEngagementEntryFormValues => ({
  _localId: generateMeiaLocalId(),
  effectId: '',
  engagementType: null,
  pkEstimate: null,
  taskTechnologyId: params?.taskTechnologyId ?? '',
  threatTargetComponentCode: null,
  threatTargetSystemCode: '',
});

export const buildEngagementEntryFromServer = (
  engagement: StudyTaskTargetEngagementSelectDto,
): MeiaTaskEngagementEntryFormValues => ({
  _localId: generateMeiaLocalId(),
  effectId: engagement.effectId,
  engagementType: engagement.engagementType ?? null,
  id: engagement.id,
  pkEstimate: engagement.pkEstimate ?? null,
  taskTechnologyId: engagement.studyTaskTechnologyId,
  threatTargetComponentCode: engagement.threatTargetComponentCode ?? null,
  threatTargetSystemCode: engagement.threatTargetSystemCode,
});

const isEngagementEntryChanged = (
  baseline: MeiaTaskEngagementEntryFormValues,
  entry: MeiaTaskEngagementEntryFormValues,
): boolean =>
  baseline.effectId !== entry.effectId ||
  baseline.engagementType !== entry.engagementType ||
  baseline.pkEstimate !== entry.pkEstimate ||
  baseline.threatTargetComponentCode !== entry.threatTargetComponentCode ||
  baseline.threatTargetSystemCode !== entry.threatTargetSystemCode;

/** Splits the form rows into create / update / move operations against the server baseline. */
export const diffEngagementEntries = (params: DiffEngagementEntriesParams): EngagementEntriesDiff => {
  const { baselineById, entries } = params;
  const diff: EngagementEntriesDiff = { creates: [], moves: [], updates: [] };

  for (const entry of entries) {
    const baseline = entry.id ? baselineById.get(entry.id) : undefined;
    if (!entry.id || !baseline) {
      diff.creates.push(entry);
      continue;
    }
    if (baseline.taskTechnologyId !== entry.taskTechnologyId) {
      diff.moves.push(entry);
      continue;
    }
    if (isEngagementEntryChanged(baseline, entry)) {
      diff.updates.push(entry);
    }
  }

  return diff;
};
